/*
 * The AI search overlay.
 *
 * Two halves, deliberately split:
 *
 *   the worker  understands the sentence. It answers with a headline, a
 *               paragraph, a ranked list of handles and a reason per handle.
 *   Liquid      owns the rows. The handles go back to Shopify through the
 *               Section Rendering API, which renders them with the price, the
 *               compare-at price, the currency and the language of the market
 *               the shopper is actually in.
 *
 * That split is the point. A price carried in the worker's answer would be the
 * price at the moment that answer was cached, in whatever currency the cache
 * was warmed with; a price rendered by Liquid is the price Shopify would put on
 * the product page in this second.
 */

(() => {
  /* Below this a question is not a question yet. */
  const MIN_QUERY_LENGTH = 3;

  /*
   * How much quiet there has to be before the search goes off by itself.
   *
   * Enter is the faster way, but nobody should have to look for it: whoever has
   * finished typing is waiting for the answer anyway. Long enough that a pause
   * in the middle of a sentence costs no model call, short enough that it does
   * not feel like waiting. The same 600 the HomeVital search runs on.
   */
  const SEARCH_DEBOUNCE_MS = 600;

  /* And the question has to be long enough: "ich" is nobody who is done. */
  const MIN_AUTO_SEARCH_LENGTH = 6;

  /* How long after the last keystroke the shop's own word search is asked. Short
     enough that it keeps up with typing. */
  const SUGGEST_DEBOUNCE_MS = 220;

  /*
   * How long the answer gets before outlines appear.
   *
   * A question that has been asked before comes back in about a hundred
   * milliseconds. Skeletons that flash up for that and vanish again read slower
   * than nothing at all - they announce a wait that is not happening.
   */
  const LOADING_DELAY_MS = 200;

  /*
   * How long the worker gets before the question is given up on.
   *
   * Measured against the live worker, a cold question is answered in about two
   * and a half seconds and the slowest of fifty was three. Ten is far outside
   * that: whatever is still running after ten seconds is not slow, it is stuck,
   * and a spinner that never stops is worse than a sentence saying so.
   *
   * It only guards the wait until the first line arrives. Once the handles are
   * there the rows are already on screen and there is nothing left to abandon.
   */
  const WORKER_TIMEOUT_MS = 10000;

  /* How many cards the theme puts up on its own. Three fill the wait and are few
     enough that the worker re-sorting them does not read as a jump. */
  const GUESS_LIMIT = 3;

  /* Below this it is a coincidence, not a match: one short word shared by two
     descriptions does not earn a card. */
  const GUESS_MIN_SCORE = 3;

  /*
   * A word on more than this share of the catalogue says nothing about any one
   * product. "bestseller" sits on all forty, "wholesale" on half - typed into
   * the field they would score the whole shop equally and the three cards would
   * be whichever three came first.
   */
  const TERM_UBIQUITY = 0.4;

  /*
   * How many hits the worker may write.
   *
   * It is not a length: the worker takes this as how wide to look and then
   * keeps what it finds relevant, so "zubehör" comes back with three of them at
   * six and with five at twelve. Six was cutting off products the shop actually
   * carries. Twelve is where ai-search-rows stops - it looks each handle up
   * through all_products, and Liquid allows twenty of those lookups per page.
   *
   * The response time barely moves with it: between six and twenty the worker
   * stayed inside two seconds either way.
   */
  const RESULT_LIMIT = 12;

  /*
   * Umlauts written out, so "grüntee" and "gruentee" are the same word. The
   * worker folds the same way - change it here and it has to change there, or
   * theme and model guess differently.
   */
  function fold(text) {
    return String(text ?? '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss');
  }

  /*
   * Words that stand in every second sentence and every second product title.
   * Both languages the shop is published in, because the field is one field.
   */
  const NOISE = new Set([
    'der', 'die', 'das', 'dem', 'den', 'des', 'ein', 'eine', 'einen', 'einem', 'einer',
    'und', 'oder', 'aber', 'mit', 'ohne', 'fuer', 'von', 'vom', 'zum', 'zur', 'auf',
    'aus', 'bei', 'nach', 'seit', 'ueber', 'unter', 'nicht', 'auch', 'noch', 'schon',
    'ich', 'mir', 'mich', 'mein', 'meine', 'meinen', 'meinem', 'meiner', 'sich',
    'hab', 'habe', 'haben', 'hat', 'bin', 'ist', 'sind', 'war', 'wird', 'werden',
    'wie', 'was', 'wann', 'warum', 'welche', 'welcher', 'welches', 'man', 'immer',
    'will', 'moechte', 'gern', 'gerne', 'etwas', 'mal', 'bitte', 'brauche', 'suche',
    'the', 'and', 'for', 'with', 'from', 'this', 'that', 'have', 'has', 'are',
    'were', 'you', 'your', 'what', 'when', 'how', 'why', 'which', 'want', 'need',
    'something', 'looking', 'would', 'like', 'some', 'any', 'please',
  ]);

  function words(text) {
    return fold(text)
      .split(/[^\p{L}\p{N}]+/u)
      .filter((word) => word.length > 2 && !NOISE.has(word));
  }

  /*
   * From here on a shared beginning counts as the same word. Five characters are
   * short enough for "matcha" inside "matchaschale" and long enough that not
   * every product comes along with every question.
   */
  const STEM_MIN = 5;

  /* Where one word stands right at the start of the other, one character less is
     enough - there the overlap is complete. */
  const STEM_SHORT = 4;

  /* The ending off, but only one: no stemmer, just enough that "schalen" is
     findable as "schale". */
  function stem(word) {
    return word.length < 5 ? word : word.replace(/(en|er|es|e|n|s)$/, '');
  }

  function sharedPrefix(a, b) {
    const end = Math.min(a.length, b.length);
    let i = 0;
    while (i < end && a[i] === b[i]) i++;
    return i;
  }

  /**
   * Whether a word occurs in the question - as part of a compound as well.
   *
   * German writes together what belongs together: whoever types "matchaschale"
   * means a bowl, and "besen" and "bambusbesen" are the same thing. Checking for
   * equality would miss most of it, because people type the word that occurs to
   * them, not the one from the list.
   */
  function matchesWord(word, asked) {
    if (asked.has(word)) return true;
    if (word.length < STEM_MIN) return false;

    const left = stem(word);

    for (const other of asked) {
      if (other.length < STEM_MIN) continue;
      if (sharedPrefix(word, other) >= STEM_MIN) return true;

      const right = stem(other);
      const shorter = left.length <= right.length ? left : right;
      const longer = left.length <= right.length ? right : left;
      if (shorter.length >= STEM_SHORT && longer.startsWith(shorter)) return true;
    }
    return false;
  }

  /*
   * Ab hier: Tippfehler.
   *
   * "macsha" ist matcha, und niemand in der Kette merkt das - der Worker nicht,
   * Shopifys eigene Suche auch nicht, beide geben auf das Wort nichts zurueck.
   * Was der Shop dagegen hat, ist seine eigene Wortliste: jedes Wort, das in
   * einem Produkttitel oder in den gepflegten Suchbegriffen steht. Ein
   * getipptes Wort, das dort nicht vorkommt, aber genau einem Wort daraus bis
   * auf einen Dreher gleicht, war dieses Wort.
   *
   * Das laeuft erst, wenn die Suche schon leer zurueckgekommen ist. Vorher
   * waere es gefaehrlich - "gesund" steht in keinem Produktnamen und wuerde zu
   * irgendetwas Naheliegendem gebogen. Nach einem leeren Ergebnis ist nichts
   * mehr zu verlieren.
   */

  /* Kuerzere Woerter nicht anfassen: bei drei Buchstaben ist jedes zweite Wort
     einen Tippfehler weit weg. */
  const CORRECT_MIN = 4;

  /* Hoechstens zwei Woerter pro Frage. Wer mehr danebentippt, meint etwas
     anderes, als der Shop fuehrt. */
  const CORRECT_MAX_WORDS = 2;

  /**
   * Abstand zweier Woerter in Tippfehlern, inklusive Buchstabendreher.
   *
   * Bricht ab, sobald die ganze Zeile ueber der Grenze liegt: gegen eine
   * Wortliste laeuft das ein paar hundert Mal pro Suche, und die weit
   * entfernten Woerter sind die grosse Mehrheit.
   */
  function editDistance(a, b, limit) {
    if (Math.abs(a.length - b.length) > limit) return limit + 1;

    let older = [];
    let previous = [];
    for (let j = 0; j <= b.length; j++) previous[j] = j;

    for (let i = 1; i <= a.length; i++) {
      const row = [i];
      let best = i;

      for (let j = 1; j <= b.length; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        let value = Math.min(previous[j] + 1, row[j - 1] + 1, previous[j - 1] + cost);

        /* Der Dreher: "macsha" gegen "matcha" ist so ein Schritt, nicht zwei. */
        if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
          value = Math.min(value, older[j - 2] + 1);
        }

        row[j] = value;
        if (value < best) best = value;
      }

      if (best > limit) return limit + 1;
      older = previous;
      previous = row;
    }

    return previous[b.length];
  }

  /** Jedes Wort, das im Katalog wirklich vorkommt. */
  function shopWords(catalog) {
    const seen = new Set();

    for (const product of catalog || []) {
      for (const word of words(product.title)) seen.add(word);
      for (const term of product.terms || []) {
        for (const word of words(term)) seen.add(word);
      }
    }

    return [...seen].filter((word) => word.length >= CORRECT_MIN);
  }

  /**
   * Dieselbe Frage, mit den Woertern, die gemeint waren.
   *
   * Gibt null zurueck, wenn nichts zu korrigieren ist - und auch dann, wenn
   * zwei Woerter aus dem Katalog gleich nah dran sind. Eine Verwechslung, die
   * geraten ist, ist schlechter als kein Treffer: der Shopper sieht beim
   * leeren Ergebnis, dass er sich vertippt hat, bei der falschen Korrektur
   * sieht er ein Sortiment, das er nicht gesucht hat.
   */
  function correctQuery(query, catalog) {
    const vocabulary = shopWords(catalog);
    if (!vocabulary.length) return null;

    const known = new Set(vocabulary);
    let changed = 0;

    const fixed = query.replace(/\p{L}+/gu, (word) => {
      if (changed >= CORRECT_MAX_WORDS) return word;

      const plain = fold(word);
      if (plain.length < CORRECT_MIN || known.has(plain) || NOISE.has(plain)) return word;

      /* Zusammengeschriebenes ist kein Tippfehler: "matchaschale" steht in
         keiner Liste und ist trotzdem richtig getippt. */
      if (vocabulary.some((entry) => entry.includes(plain) || plain.includes(entry))) return word;

      /*
       * Ab sechs Buchstaben sind zwei Tippfehler erlaubt - "macsha" ist von
       * "matcha" genau so weit weg. Zwei Fehler machen aber auch aus jedem
       * anderen Wort eines aus dem Katalog: "schuhe" wird zu "schale", "hose"
       * zu "dose". Deshalb zaehlt nicht nur der Abstand, sondern auch, wo er
       * liegt: wer sich vertippt, vertippt sich in der Mitte. Anfang und Ende
       * bleiben stehen, und genau daran ist der Vertipper vom anderen Wort zu
       * unterscheiden.
       */
      const limit = plain.length >= 6 ? 2 : 1;
      const plausible = (entry, gap) =>
        gap <= 1
          ? entry[0] === plain[0]
          : entry.slice(0, 2) === plain.slice(0, 2) && entry.slice(-2) === plain.slice(-2);

      let best = null;
      let closest = limit + 1;
      let ambiguous = false;

      for (const entry of vocabulary) {
        const gap = editDistance(plain, entry, limit);
        if (gap > limit || !plausible(entry, gap)) continue;
        if (gap < closest) {
          closest = gap;
          best = entry;
          ambiguous = false;
        } else if (gap === closest && entry !== best) {
          ambiguous = true;
        }
      }

      if (!best || ambiguous) return word;
      changed += 1;
      return best;
    });

    return changed ? fixed : null;
  }

  /**
   * The words of the catalogue that say something about one product.
   *
   * The shop's tags carry its bookkeeping as well as its vocabulary - "abo",
   * "wholesale", "19%", a handful of Shopify's own structured tags. Counting how
   * many products a word sits on separates the two without anybody having to
   * keep a list: a word on nearly everything cannot tell one product from
   * another, whatever it means.
   */
  function usefulTerms(catalog) {
    const seen = new Map();

    for (const product of catalog) {
      for (const term of new Set(product.terms || [])) {
        seen.set(term, (seen.get(term) || 0) + 1);
      }
    }

    const ceiling = Math.max(2, Math.floor(catalog.length * TERM_UBIQUITY));

    return catalog.map((product) => ({
      ...product,
      terms: (product.terms || []).filter(
        (term) => term && !term.includes(':') && !term.includes('%') && seen.get(term) <= ceiling
      ),
    }));
  }

  /**
   * The theme's own guess at which products fit this sentence.
   *
   * No model, only the words the shop maintains anyway. A whole phrase weighs
   * heavily ("matcha ohne koffein" is unambiguous), a single word little
   * ("matcha" is on everything). What comes out is approximate - and it is there
   * in no milliseconds instead of a second and a half.
   */
  function guessProducts(query, catalog) {
    if (!Array.isArray(catalog) || !catalog.length) return [];

    const haystack = ` ${fold(query).replace(/[^\p{L}\p{N}]+/gu, ' ').trim()} `;
    const asked = new Set(words(query));
    if (!asked.size) return [];

    return catalog
      .map((product) => {
        let score = 0;

        for (const term of product.terms || []) {
          const phrase = fold(term).replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
          if (!phrase) continue;

          if (haystack.includes(` ${phrase} `)) {
            score += 3 + phrase.split(' ').length;
            continue;
          }

          /* A long word is its own proof: "koffeinfrei" stands in no sentence by
             accident, "tee" does. */
          for (const word of phrase.split(' ')) {
            if (word.length > 3 && matchesWord(word, asked)) score += word.length >= 8 ? 3 : 1;
          }
        }

        /*
         * The title carries the most weight here, and it has to: this shop has
         * no field of search words behind it, only tags it keeps for its own
         * bookkeeping. A long word out of a product name is the strongest thing
         * left - "koffeinfrei" stands in no question by accident, "tee" does.
         */
        for (const word of words(product.title)) {
          if (matchesWord(word, asked)) score += word.length >= 8 ? 3 : 2;
        }

        return { product, score };
      })
      .filter((entry) => entry.score >= GUESS_MIN_SCORE)
      .sort((a, b) => b.score - a.score)
      .slice(0, GUESS_LIMIT)
      .map((entry) => entry.product);
  }

  function rootUrl() {
    return window.Shopify?.routes?.root || '/';
  }

  /**
   * The one reason the worker wrote for the whole question, if that is what it
   * wrote.
   *
   * The response has a reason per product, but the model fills them all with
   * the same sentence: "passt: matcha besen" under six rows, once for each. It
   * is not a justification of any one product, it is the question read back -
   * useful once, noise six times. So it is pulled out here and put above the
   * answer instead, and the rows keep their line only when the reasons actually
   * differ between them.
   */
  function sharedReason(products) {
    const lines = products.map((product) =>
      (product.reasons || []).filter(Boolean).join(' · ').trim()
    );

    if (lines.length < 2 || lines.some((line) => !line)) return null;
    return lines.every((line) => line === lines[0]) ? lines[0] : null;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * The configuration sits on the page once, as JSON, from
   * snippets/ai-search-config.liquid. A broken entry must not take the search
   * with it - then the empty default applies and the field still works.
   */
  let configCache = null;

  function config() {
    if (configCache) return configCache;

    const node = document.getElementById('ai-search-config');
    if (!node) {
      configCache = {};
      return configCache;
    }

    try {
      configCache = JSON.parse(node.textContent) || {};
    } catch (error) {
      console.warn('ai-search: configuration is not valid JSON', error);
      configCache = {};
    }
    return configCache;
  }

  function searchUrl(query) {
    const base = config().rowsUrl || '/search';
    return `${base}?q=${encodeURIComponent(query)}`;
  }

  /* ---------- Auswertung ---------- */

  /*
   * Was mit den Antworten passiert, für das Dashboard des Workers (/stats).
   * Jede Frage bekommt hier eine search_id, die als `sid` mit zum Worker geht -
   * der schreibt Frage und Antwort darunter auf. Was danach passiert, geht als
   * Beacon an /track. Keine Cookies, keine ID über die Frage hinaus.
   *
   * Alles hier ist Beiwerk: es wirft nie, und eine verlorene Meldung kostet
   * keine Suche.
   */
  const STAGE_NAMES = ['idle', 'guess', 'partial', 'final'];

  function newSearchId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  }

  function track(type, detail = {}) {
    try {
      const { endpoint, shop } = config();
      if (!endpoint || !shop) return;

      const url = new URL('track', new URL(endpoint, window.location.origin)).toString();
      const body = JSON.stringify({ shop, type, ...detail });

      /* text/plain, damit der Browser nicht erst per OPTIONS fragt - und als
         Beacon, damit die Meldung auch rausgeht, wenn der Klick die Seite
         gerade verlässt. */
      if (navigator.sendBeacon?.(url, body)) return;
      fetch(url, { method: 'POST', body, keepalive: true, mode: 'no-cors' }).catch(() => {});
    } catch (error) {
      /* Zählen ist Beiwerk. */
    }
  }

  /*
   * Die Frage, aus der ein Produkt kam, wandert als Warenkorb-Attribut mit bis
   * in die Bestellung. Der Checkout-Pixel meldet sie von dort als Kauf - so
   * zählt auch, wer erst auf der Produktseite in den Warenkorb legt. Der
   * Unterstrich hält das Attribut aus dem Checkout heraus.
   */
  function rememberSearch(searchId) {
    if (!searchId) return;
    try {
      fetch(`${rootUrl()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { _ai_search: searchId } }),
        keepalive: true,
      }).catch(() => {});
    } catch (error) {
      /* Zählen ist Beiwerk. */
    }
  }

  function handleFromUrl(href) {
    const match = /\/products\/([^/?#]+)/.exec(href || '');
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch (error) {
      return match[1];
    }
  }

  class SearchEngine {
    /* `source` sagt der Auswertung, wo gefragt wurde: overlay oder page. */
    constructor(source) {
      this.controller = null;
      this.source = source;
      this.searchId = null;
    }

    /** Drops a running request - nobody wants the answer to the old question. */
    abort() {
      clearTimeout(this.watchdog);
      this.controller?.abort();
      this.controller = null;
      this.asking = false;
    }

    /*
     * The same, but only for the worker.
     *
     * Whoever types on has withdrawn the question before. The suggestion request
     * is left alone - that one is meant to keep up with the typing.
     */
    abortAsk() {
      if (this.asking) this.abort();
    }

    /**
     * Shopify's own suggestions. They know the catalogue but no sentences - in
     * return they are there immediately.
     */
    async suggest(query) {
      this.abort();
      this.controller = new AbortController();
      this.asking = false;

      const url = new URL(`${rootUrl()}search/suggest.json`, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product,query');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok) throw new Error(`suggest responded ${response.status}`);

      const data = await response.json();
      return data?.resources?.results ?? { queries: [], products: [] };
    }

    /**
     * The shop's vocabulary, fetched once and kept.
     *
     * It is what the theme guesses with, and it costs one local request the
     * first time the search is opened rather than a few kilobytes on every page
     * of the shop. Failing is allowed: without it there are no guesses, and the
     * outlines take over the way they did before.
     */
    async catalog() {
      if (this.catalogue) return this.catalogue;
      if (this.catalogueRequest) return this.catalogueRequest;

      const { rowsUrl } = config();
      if (!rowsUrl) return [];

      const url = new URL(rowsUrl, window.location.origin);
      url.searchParams.set('q', 'x');
      url.searchParams.set('section_id', 'ai-search-catalog');

      this.catalogueRequest = fetch(url.toString())
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const node = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('[data-ai-catalog]');
          const parsed = node ? JSON.parse(node.textContent) : [];
          this.catalogue = usefulTerms(Array.isArray(parsed) ? parsed : []);
          return this.catalogue;
        })
        .catch((error) => {
          console.warn('ai-search: catalogue could not be read', error);
          this.catalogue = [];
          return this.catalogue;
        });

      return this.catalogueRequest;
    }

    /**
     * The question to the worker.
     *
     * 404 is an answer there, not a failure: "nothing matches" arrives with an
     * empty handles list, and that is exactly what the shopper wants to know.
     * A body without a handles array is something else - an unknown shop, a
     * configuration error - and must never be shown as "we do not carry that",
     * because that sentence would then be a lie about the whole catalogue.
     */
    async ask(query, onPartial, { via, retryOf } = {}) {
      /* Gleich zu Beginn, nicht erst mit der Antwort: wer auf eine Zeile der
         Vermutung klickt, während das Modell noch schreibt, klickt schon auf
         diese Frage. */
      const searchId = newSearchId();
      this.searchId = searchId;

      const check = (data) => {
        if (!Array.isArray(data?.handles)) {
          throw new Error(data?.error || 'search worker returned no handles');
        }
        data.searchId = searchId;
        return data;
      };

      this.abort();
      this.controller = new AbortController();
      this.asking = true;

      /*
       * The wait is given a limit. `abort()` is the same one the typing uses, so
       * the flag is what tells the two apart afterwards - a cancelled question
       * is silent, a timed-out one owes the shopper a sentence.
       */
      this.timedOut = false;
      this.watchdog = setTimeout(() => {
        this.timedOut = true;
        this.abort();
      }, WORKER_TIMEOUT_MS);

      const { endpoint, shop, language } = config();
      if (!endpoint || !shop) throw new Error('ai-search: endpoint or shop missing');

      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('query', query);
      url.searchParams.set('shop', shop);
      url.searchParams.set('limit', String(RESULT_LIMIT));
      url.searchParams.set('answer', '1');
      /*
       * The handles stand in the model's JSON before the sentence does. Whoever
       * passes a callback gets them twice: the list as soon as it is settled,
       * and the same answer again with the headline and the reasons.
       */
      if (typeof onPartial === 'function') url.searchParams.set('stream', '1');
      if (language) url.searchParams.set('language', language);
      /* Nur für die Auswertung - der Cache des Workers sieht diese Parameter nicht. */
      url.searchParams.set('sid', searchId);
      if (this.source) url.searchParams.set('src', this.source);
      if (via) url.searchParams.set('via', via);
      if (retryOf) url.searchParams.set('retry_of', retryOf);
      url.searchParams.set('path', window.location.pathname);

      // As a string, because several apps hook window.fetch and treat the first
      // argument as one.
      const response = await fetch(url.toString(), { signal: this.controller.signal });
      if (!response.ok && response.status !== 404) {
        throw new Error(`search worker responded ${response.status}`);
      }

      const streamed = (response.headers.get('Content-Type') || '').includes('ndjson');
      if (!streamed || !response.body) {
        clearTimeout(this.watchdog);
        return check(await response.json());
      }

      /*
       * One line per state. A network cuts in the middle of text, so bytes are
       * collected until a newline arrives - whatever follows is the beginning of
       * the next line and stays put.
       */
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let last = null;

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let cut = buffer.indexOf('\n');
        while (cut !== -1) {
          const line = buffer.slice(0, cut).trim();
          buffer = buffer.slice(cut + 1);
          cut = buffer.indexOf('\n');
          if (!line) continue;

          let event;
          try {
            event = JSON.parse(line);
          } catch {
            continue;
          }

          /* Something arrived, so nothing is stuck. The sentence that follows
             the handles may take its time from here on. */
          clearTimeout(this.watchdog);

          if (event.type === 'error') {
            throw new Error(event.payload?.error || 'search worker failed');
          }
          if (event.type === 'partial') {
            if (Array.isArray(event.payload?.handles)) onPartial(event.payload);
            continue;
          }
          last = event.payload;
        }
      }

      if (!last) throw new Error('search worker returned no answer');
      return check(last);
    }

    /**
     * The rows for those handles, rendered by the theme's own section.
     *
     * The signal is passed in rather than taken from the running request: the
     * theme's own guess asks for rows a moment before the worker is asked
     * anything, and hanging it on the worker's controller would have the next
     * ask() cancel the very rows it was meant to fill the wait with.
     */
    async rows(handles, signal) {
      const { rowsUrl, rowsSection } = config();
      if (!rowsUrl || !rowsSection || handles.length === 0) return '';

      const url = new URL(rowsUrl, window.location.origin);
      url.searchParams.set('q', handles.join(' '));
      url.searchParams.set('section_id', rowsSection);

      const response = await fetch(url.toString(), signal ? { signal } : undefined);
      if (!response.ok) throw new Error(`section rendering responded ${response.status}`);

      return response.text();
    }
  }

  /* What is on screen, in the order of how much it is worth. */
  const STAGE = { idle: 0, guess: 1, partial: 2, final: 3 };

  /**
   * Warning words: a question that no product list is the answer to. The needle
   * has to stand as a word in the sentence, not inside another one.
   */
  function matchesFlag(query, flags) {
    const haystack = ` ${fold(query).replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim()} `;

    /* Eine Zeile bringt mehrere Begriffe mit, kommagetrennt wie bei den
       Rückfragen: "wo ist meine bestellung" ist derselbe Fall wie "tracking",
       und niemand soll dafür fünf Zeilen pflegen. */
    return flags.find((flag) =>
      String(flag.match || '')
        .split(',')
        .map((wort) => fold(wort).replace(/[^\p{L}\p{N}\s]/gu, ' ').replace(/\s+/g, ' ').trim())
        .filter(Boolean)
        .some((wort) => haystack.includes(` ${wort} `))
    );
  }

  /**
   * Follow-up questions: words that on their own are too wide. "geschenk" fits
   * four products for three different people - a question is more honest there
   * than a list.
   */
  function matchesClarifier(query, clarifiers) {
    const normalized = fold(query).trim();
    return clarifiers.find((entry) =>
      (entry.match || '')
        .split(',')
        .map((word) => fold(word).trim())
        .filter(Boolean)
        .some((word) => normalized === word || normalized === `${word}?`)
    );
  }

  class AiSearchOverlay extends HTMLElement {
    constructor() {
      super();

      this.engine = new SearchEngine('overlay');

      this.onClick = this.handleClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onInput = this.handleInput.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onResize = this.measureHeader.bind(this);

      this.searchTimer = null;
      this.suggestTimer = null;
      this.loadingTimer = null;

      /*
       * How good what stands on screen is, so nothing ever replaces something
       * better: the theme's guess must not land on top of the worker's answer
       * just because the local request came back second.
       */
      this.stage = STAGE.idle;
      this.renderedHandles = '';
    }

    connectedCallback() {
      this.input = this.querySelector('[data-ai-input]');
      this.clearButtons = this.querySelectorAll('[data-ai-clear]');
      this.results = this.querySelector('[data-ai-results]');
      this.rail = this.querySelector('[data-ai-rail]');

      this.texts = config().texts || {};
      this.flags = config().flags || [];
      this.clarifiers = config().clarifiers || [];

      /* The entry state comes rendered from Liquid. Keeping a copy means
         clearing the field restores exactly that, instead of the script
         rebuilding markup Liquid already knows how to write. */
      this.idleMarkup = this.results ? this.results.innerHTML : '';

      /* The tiles are kept separately because the zero-result state shows them
         again: a question nobody has an answer to is exactly the moment to
         offer the questions that do have one. */
      this.tilesMarkup = this.querySelector('[data-ai-tiles]')?.outerHTML || '';
      this.lastQuery = '';

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.input?.addEventListener('input', this.onInput);
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('resize', this.onResize);

      this.measureHeader();
      this.adoptHeaderLogo();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.input?.removeEventListener('input', this.onInput);
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('resize', this.onResize);
      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestTimer);
      this.clearLoading();
      this.engine.abort();
    }

    /* ---------- the header it stands in for ---------- */

    /**
     * The overlay covers the header and nothing above it.
     *
     * Both numbers are read off the real header rather than written down: the
     * merchant sets the logo height and the header padding in the editor, and a
     * number copied from the artboard into the stylesheet would be wrong the
     * day either changes. `top` is where the header starts in the viewport -
     * under the announcement bar while that is on screen, at zero once the
     * sticky header has travelled to the top - so the bar stays visible and
     * nothing on the page moves when the search opens.
     */
    measureHeader() {
      const section = document.querySelector('.shopify-section-header');
      if (!section) return;

      const rect = section.getBoundingClientRect();
      if (rect.height === 0) return;

      /*
       * Three measurements, because the overlay sits differently on the two
       * sizes.
       *
       * On a desktop it stands in front of the header: it starts where the
       * section starts, under the announcement bar, and its own row is exactly
       * as tall as the header's.
       *
       * On a phone it does not. The shop's header stays on screen - burger,
       * logo, cart, all of it - and the overlay hangs underneath, with its field
       * exactly where the shop's search bar is. So it starts at the bottom of
       * the logo row and its own row is as tall as the bar it covers.
       */
      const row = section.querySelector('.header');
      const bar = section.querySelector('.header__mobile-search');

      const rowRect = row ? row.getBoundingClientRect() : rect;
      const barRect = bar && bar.offsetParent ? bar.getBoundingClientRect() : null;

      this.style.setProperty('--ai-search-top', `${Math.max(0, Math.round(rect.top))}px`);
      this.style.setProperty(
        '--ai-search-header-height',
        `${Math.round(rowRect.height) || Math.round(rect.height)}px`
      );

      /* Without the bar there is nothing to hang under, and the overlay falls
         back to covering the header the way it does on a desktop. */
      this.style.setProperty(
        '--ai-search-row-bottom',
        `${Math.max(0, Math.round(barRect ? rowRect.bottom : rect.top))}px`
      );
      this.style.setProperty(
        '--ai-search-bar-height',
        `${Math.round(barRect ? barRect.height : rowRect.height)}px`
      );
    }

    /**
     * The shop's logo, taken from the header itself.
     *
     * The theme setting in the overlay markup is a fallback: pointing a second
     * setting at a logo file is how the two drift apart, and the one thing the
     * overlay must not do is open with yesterday's logo on it. The inverse copy
     * is skipped - that one belongs to the transparent header, and the overlay
     * is white.
     */
    adoptHeaderLogo() {
      const slot = this.querySelector('[data-ai-logo]');
      if (!slot) return;

      const header = document.querySelector('.shopify-section-header');
      const source = header?.querySelector(
        '.header__heading-link img.header__heading-logo:not(.inverse):not(.medium-hide)'
      );
      if (!source) return;

      const logo = source.cloneNode(false);
      logo.className = 'ai-search__logo-image';
      logo.removeAttribute('loading');
      logo.removeAttribute('sizes');
      slot.replaceChildren(logo);

      /* What the header actually renders it at. Behind a transparent header the
         normal logo is hidden and measures zero, which is what the variable the
         header sets for its own logo is for. */
      const rendered = Math.round(source.getBoundingClientRect().height);
      const declared = getComputedStyle(header).getPropertyValue('--image-logo-height').trim();

      if (rendered > 0) {
        this.style.setProperty('--ai-search-logo-height', `${rendered}px`);
      } else if (declared) {
        this.style.setProperty('--ai-search-logo-height', declared);
      }
    }

    handleDocumentClick(event) {
      /*
       * Nothing inside the overlay opens the overlay. Without this the whole
       * panel counted as a trigger the moment it was open, because the marker
       * that locks the page behind it sat on <body> - and closest() walks all
       * the way up there. Every link in a result row was then cancelled by the
       * preventDefault below instead of leading to its product.
       */
      if (this.contains(event.target)) return;

      const trigger = event.target.closest?.('[data-ai-search-open]');
      if (!trigger) return;

      /* The header search sits in a <details>; without this the native modal
         would open underneath the overlay. */
      event.preventDefault();
      this.open(trigger.dataset.aiSearchQuery || '');
    }

    handleClick(event) {
      if (event.target.closest('[data-ai-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.target.closest('[data-ai-clear]')) {
        event.preventDefault();
        this.clear();
        return;
      }

      const tile = event.target.closest('[data-ai-query]');
      if (tile) {
        event.preventDefault();
        this.fill(tile.dataset.aiQuery);
        this.run(tile.dataset.aiQuery, tile.classList.contains('ai-search__suggestion') ? 'suggestion' : 'tile');
        return;
      }

      const add = event.target.closest('[data-ai-add]');
      if (add) {
        event.preventDefault();
        this.addToCart(add);
        return;
      }

      const link = event.target.closest('a[href]');
      if (link) this.trackLink(link);
    }

    handleKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key === 'Enter' && event.target === this.input) {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        const value = this.input.value.trim();
        /* The same question that is already answered (or on its way) is not
           asked again: run() starts from zero, and the results on screen blinked
           away and came back identical - on a phone it flashed the whole page. */
        const alreadyAsked = value === this.lastQuery && this.stage > STAGE.idle;
        if (value.length >= MIN_QUERY_LENGTH && !alreadyAsked) this.run(value, 'enter');

        /* On a phone the search key on the keyboard is the way to say "done".
           The answer has usually started on its own by then, so without this
           the key looked dead: nothing new to run, and the keyboard stayed up
           over the results. Blurring the field puts it away. On a desktop the
           cursor stays, someone there may want to keep typing. */
        if (window.matchMedia('(hover: none)').matches) this.input.blur();
      }
    }

    handleInput() {
      const value = this.input.value.trim();
      this.clearButtons.forEach((button) => button.toggleAttribute('data-show', value.length > 0));

      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestTimer);

      /* Whoever types on is no longer waiting for the answer to the sentence
         before. The worker finishes it and keeps it in its cache either way -
         this only frees the line and the stream for the question that is
         coming. The suggestion request stays, it is meant to keep up. */
      if (value !== this.lastQuery) {
        this.engine.abortAsk();
        this.stage = STAGE.idle;
        this.renderedHandles = '';
        this.clearLoading();
      }

      if (value.length < MIN_QUERY_LENGTH) {
        this.lastQuery = '';
        this.showIdle();
        return;
      }

      /* The shop's own word search knows no sentences, but it knows product
         names, and it is there while the model is still reading. */
      this.suggestTimer = setTimeout(() => this.showSuggestions(value), SUGGEST_DEBOUNCE_MS);

      /* And whoever stops typing has asked. */
      if (value.length >= MIN_AUTO_SEARCH_LENGTH && value !== this.lastQuery) {
        this.searchTimer = setTimeout(() => this.run(value, 'typed'), SEARCH_DEBOUNCE_MS);
      }
    }

    open(query = '') {
      /* Already open: a tap on the header search, or into the bar above the
         overlay on a phone, brings the cursor back here instead of leaving it
         in a field nobody listens to. */
      if (this.hasAttribute('data-show')) {
        this.input?.focus();
        return;
      }

      this.previousFocus = document.activeElement;
      this.pageScrollY = window.scrollY;
      this.measureHeader();
      this.adoptHeaderLogo();
      this.setAttribute('data-show', '');
      document.body.toggleAttribute('data-ai-search-locked', true);

      /* Fetched now, not when the first sentence is done: it is one local
         request and it decides whether the wait has content in it. */
      this.engine.catalog();

      if (query) {
        this.fill(query);
        this.run(query, 'link');
      }

      /* On a phone focus() brings the keyboard up, which is the point: the
         overlay exists to be typed into. */
      this.input?.focus();
    }

    close() {
      if (!this.hasAttribute('data-show')) return;

      this.removeAttribute('data-show');
      document.body.toggleAttribute('data-ai-search-locked', false);
      this.lastLocal = null;
      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestTimer);
      this.clearLoading();
      this.engine.abort();

      /* Focus goes back where it came from, for keyboard users - except into
         the header search field. That field is how the overlay was opened;
         on a phone focusing it brings the keyboard back up, and typing there
         went to the shop's old search instead of this one. */
      const back = this.previousFocus;
      if (back?.focus && !back.matches?.('[data-search-trigger]')) back.focus();
      else document.activeElement?.blur?.();
    }

    fill(query) {
      if (!this.input) return;

      this.input.value = query;
      this.handleInput();
      this.input.focus();
    }

    clear() {
      if (!this.input) return;

      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestTimer);
      this.clearLoading();
      this.engine.abort();
      this.input.value = '';
      this.lastQuery = '';
      this.handleInput();
      this.showIdle();
      this.input.focus();
    }

    /* ---------- states ---------- */

    showIdle() {
      this.clearLoading();
      this.stage = STAGE.idle;
      this.renderedHandles = '';
      if (this.results) this.results.innerHTML = this.idleMarkup;
    }

    /**
     * What Shopify's own search makes of the words so far.
     *
     * It does not understand the sentence - that is the normal case for a
     * description and nothing anybody needs to be shown. When it finds nothing,
     * the entry state stays where it is until the search itself goes off.
     */
    async showSuggestions(query) {
      if (this.stage > STAGE.idle) return;

      let results;
      try {
        results = await this.engine.suggest(query);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        return;
      }

      if (this.input.value.trim() !== query || this.stage > STAGE.idle) return;

      const queries = (results.queries || []).slice(0, 3);
      /* "Produkte dazu" is the shop's word search on the first letters, before
         the answer. On a phone it filled the screen with near misses ("Ich"
         finds Hojicha) right under the keyboard, so it stays a desktop thing. */
      const products = window.matchMedia('(max-width: 899px)').matches
        ? []
        : (results.products || []).slice(0, 3);
      if (!queries.length && !products.length) return;

      const suggestion = (text, label) => `
        <button type="button" class="ai-search__suggestion" data-ai-query="${escapeHtml(text)}">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
            <circle cx="10.5" cy="10.5" r="7" stroke="currentColor" stroke-width="1.4"/>
            <path d="M15.6 15.6 21 21" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
          </svg>
          <span>${label}</span>
        </button>`;

      this.clearLoading();
      this.results.innerHTML = `
        ${
          queries.length
            ? `<span class="ai-search__label">${escapeHtml(this.texts.suggestLabel || '')}</span>
               <div class="ai-search__suggestions">
                 ${queries
                   .map((entry) => suggestion(entry.text, entry.styled_text || escapeHtml(entry.text)))
                   .join('')}
               </div>`
            : ''
        }
        ${
          products.length
            ? `<span class="ai-search__label">${escapeHtml(this.texts.productsLabel || '')}</span>
               <div class="ai-search__suggestions">
                 ${products.map((product) => suggestion(product.title, escapeHtml(product.title))).join('')}
               </div>`
            : ''
        }
        ${this.enterHint()}
      `;
    }

    enterHint() {
      if (!this.texts.enterHint) return '';
      return `
        <span class="ai-search__enter-hint">
          <span class="ai-search__key">${escapeHtml(this.texts.enterKey || 'Enter')}</span>
          <span>${escapeHtml(this.texts.enterHint)}</span>
        </span>`;
    }

    /*
     * The model writes for a second or two. A spinner saying "we are looking"
     * turns that into an eternity; the outline of what is coming turns it into
     * loading. And neither is shown at all until it is clear that there is
     * something to wait for.
     */
    showLoading() {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = setTimeout(() => this.paintLoading(), LOADING_DELAY_MS);
    }

    /* Takes the outlines away again as soon as real content arrives - including
       when it was there faster than they meant to appear. */
    clearLoading() {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
    }

    paintLoading() {
      if (this.stage > STAGE.idle) return;

      this.results.innerHTML = `
        ${this.answerBones()}
        <div class="ai-search__rows">
          <span class="ai-search__skeleton"></span>
          <span class="ai-search__skeleton"></span>
          <span class="ai-search__skeleton"></span>
        </div>
      `;
    }

    /*
     * The answer block while it is still being written. Same place and roughly
     * the same height as the finished one, so the rows below it do not jump when
     * the sentence lands.
     */
    answerBones() {
      return `
        <div class="ai-search__answer" data-ai-answer data-pending role="status">
          <span class="ai-search__eyebrow">
            <span class="ai-search__spinner" aria-hidden="true"></span>
            <span>${escapeHtml(this.texts.loading || '')}</span>
          </span>
          <span class="ai-search__bone ai-search__bone--heading"></span>
          <span class="ai-search__bone ai-search__bone--text"></span>
        </div>
      `;
    }

    /**
     * A question a product list is the wrong answer to. It never waits for the
     * worker: whoever types one of these must not be read a shopping list first
     * and the sentence that matters a keystroke later.
     */
    showFlag(flag) {
      this.clearLoading();
      this.stage = STAGE.final;
      this.results.innerHTML = `
        <div class="ai-search__flag" role="alert">
          <span class="ai-search__eyebrow">${escapeHtml(this.texts.flagLabel || '')}</span>
          <h2 class="ai-search__answer-heading">${escapeHtml(flag.heading || '')}</h2>
          <p class="ai-search__answer-text">${escapeHtml(flag.text || '')}</p>
          ${
            flag.link
              ? `<a class="ai-search__flag-link" href="${escapeHtml(flag.link.url)}">
                   ${escapeHtml(flag.link.text)}
                 </a>`
              : ''
          }
        </div>
      `;
    }

    /** A word too wide to answer. The shop asks back instead of guessing. */
    showClarifier(clarifier) {
      this.clearLoading();
      this.stage = STAGE.final;

      const options = (clarifier.options || [])
        .map(
          (option) => `
            <button type="button" class="ai-search__tile" data-ai-query="${escapeHtml(option.query)}">
              <span class="ai-search__info">
                <span class="ai-search__title">${escapeHtml(option.title)}</span>
                ${option.note ? `<span class="ai-search__note">${escapeHtml(option.note)}</span>` : ''}
              </span>
            </button>`
        )
        .join('');

      this.results.innerHTML = `
        <div class="ai-search__answer" data-ai-answer>
          <span class="ai-search__eyebrow">${escapeHtml(this.texts.clarifyLabel || '')}</span>
          <h2 class="ai-search__answer-heading">${escapeHtml(clarifier.heading || '')}</h2>
          ${clarifier.text ? `<p class="ai-search__answer-text">${escapeHtml(clarifier.text)}</p>` : ''}
        </div>
        <div class="ai-search__tiles">${options}</div>
      `;
    }

    /* Never a blank page: the shop says what it does not have, and then hands
       back the six questions it does answer. */
    showEmpty(searchId) {
      track('empty', { sid: searchId });
      this.clearLoading();
      this.stage = STAGE.final;
      this.renderedHandles = '';
      this.results.innerHTML = `
        <div class="ai-search__intro">
          <span class="ai-search__label">${escapeHtml(this.texts.emptyLabel)}</span>
          <h2 class="ai-search__heading">${escapeHtml(this.texts.emptyHeading)}</h2>
          <p class="ai-search__text">${escapeHtml(this.texts.emptyText)}</p>
        </div>
        ${this.tilesMarkup}
      `;
    }

    /* The worker being unreachable is not the end of searching: the shop's own
       search does not understand sentences but it does find product names. */
    showError(query) {
      track('error', { sid: this.engine.searchId });
      this.clearLoading();
      this.stage = STAGE.final;
      this.renderedHandles = '';
      this.results.innerHTML = `
        <div class="ai-search__intro">
          <p class="ai-search__text">${escapeHtml(this.texts.errorText)}</p>
        </div>
        <a class="ai-search__button" href="${escapeHtml(searchUrl(query))}">
          <span class="ai-search__button-label">${escapeHtml(this.texts.errorButton)}</span>
        </a>
      `;
    }

    answerBlock(data, shared) {
      const answer = data.answer;
      if (!answer || !answer.headline) return '';

      /* Keine Zeile "Wir haben gesucht nach …" mehr: das "Gesucht:" oben sagt
         schon, was verstanden wurde, und die Tippfehler-Korrektur des Workers
         lag oft daneben ("anfangen" wurde "anfaenger"). */
      return `
        <div class="ai-search__answer" data-ai-answer>
          <span class="ai-search__eyebrow">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
              <path d="M9.4 17.5h5.2M10.2 20.5h3.6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              <path d="M12 3.5a5.6 5.6 0 0 1 3.4 10.1c-.6.5-.9 1.1-.9 1.8H9.5c0-.7-.3-1.3-.9-1.8A5.6 5.6 0 0 1 12 3.5Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
            </svg>
            <span>${escapeHtml(
              shared
                ? `${this.texts.understoodPrefix || ''} ${this.stripReasonPrefix(shared)}`.trim()
                : this.texts.answerLabel
            )}</span>
          </span>
          <h2 class="ai-search__answer-heading">${escapeHtml(answer.headline)}</h2>
          ${answer.body ? `<p class="ai-search__answer-text">${escapeHtml(answer.body)}</p>` : ''}
        </div>
      `;
    }

    /**
     * The search itself: first the warning list, then the follow-up question,
     * then the shop's own guess, then the worker.
     *
     * The order is the ranking. Everything after the first two arrives in three
     * goes, each better than the one before: the theme's guess in no time at
     * all, the worker's list as soon as the model has written it, and the same
     * list again with the sentence above it and a reason per row. Each step
     * checks the question is still the current one - a shopper who types on
     * while the model thinks must not be shown the answer to the sentence they
     * have already moved past.
     */
    async run(query, via = 'typed') {
      /* Every search comes through here, so a timer still counting down is
         asking the question that is already on its way. A tile is the case:
         it fills the field, which arms the timer, and searches in the same
         breath. */
      clearTimeout(this.searchTimer);
      clearTimeout(this.suggestTimer);

      /* A new question starts reading at the top. Tapping a tile or a
         suggestion far down the list left the scroll position there, and the
         answer arrived above the fold with the view parked at its bottom. Once
         now, and once more when the first result for it is on screen. */
      this.topFor = query;
      this.scrollListToTop();

      this.lastQuery = query;
      this.stage = STAGE.idle;
      this.renderedHandles = '';

      const flag = matchesFlag(query, this.flags);
      if (flag) {
        this.trackLocal('flag', query);
        return this.showFlag(flag);
      }

      const clarifier = matchesClarifier(query, this.clarifiers);
      if (clarifier) {
        this.trackLocal('clarify', query);
        return this.showClarifier(clarifier);
      }

      /*
       * The outlines are for the case where the theme has nothing of its own to
       * show. With the catalogue already in memory the guess decides that in no
       * time, and grey bars that appear for three hundred milliseconds and are
       * gone again read worse than the wait itself - so they are only put up
       * front while the catalogue is still on its way.
       */
      if (!this.engine.catalogue) this.showLoading();
      this.guess(query);

      let data;
      try {
        data = await this.engine.ask(
          query,
          (partial) => {
            /* The rows stand as soon as the model has written the handles. The
               sentence above them stays an outline - it comes half a second
               later, and by then there is already something to read. */
            if (this.lastQuery === query && partial.handles.length) {
              this.present(query, partial, STAGE.partial);
            }
          },
          { via }
        );
      } catch (error) {
        /* Whoever typed on withdrew the question and gets nothing. A question
           that ran out of time is the opposite: nobody withdrew it, so it owes
           an answer - and the one sentence the theme has for this is true here. */
        if (error?.name === 'AbortError' && !this.engine.timedOut) return;
        console.warn('ai-search: worker unreachable', error);
        if (this.lastQuery === query) this.showError(query);
        return;
      }

      if (this.lastQuery !== query) return;

      if (!data.handles.length) {
        if (await this.retryCorrected(query)) return;
        this.showEmpty(data.searchId);
        return;
      }

      await this.present(query, data, STAGE.final);
    }

    /**
     * Zweiter Anlauf mit dem Wort, das gemeint war.
     *
     * Nur nach einem leeren Ergebnis, nur einmal, und nur wenn die Wortliste
     * des Shops eindeutig sagt, welches Wort da danebengetippt wurde. Kommt
     * auch darauf nichts zurueck, bleibt es bei der leeren Antwort auf die
     * Frage, die wirklich getippt wurde.
     */
    async retryCorrected(query) {
      let fixed = null;
      try {
        fixed = correctQuery(query, await this.engine.catalog());
      } catch (error) {
        return false;
      }

      if (!fixed || fold(fixed) === fold(query)) return false;
      if (this.lastQuery !== query) return false;

      let data;
      try {
        data = await this.engine.ask(fixed, undefined, { via: 'retry', retryOf: this.engine.searchId });
      } catch (error) {
        return false;
      }

      if (this.lastQuery !== query || !data.handles.length) return false;

      /* Ab hier ist die korrigierte Frage die aktuelle - sonst weist present
         die Antwort ab, die es gerade selbst geholt hat. */
      this.lastQuery = fixed;
      this.stage = STAGE.idle;
      this.renderedHandles = '';

      await this.present(fixed, { ...data, corrected_from: query }, STAGE.final);
      return true;
    }

    /**
     * What the theme itself thinks fits, before anything goes over the wire.
     *
     * Not the answer - it says so on every row and is replaced or re-sorted a
     * moment later - but real content where grey bars used to stand for a
     * second and a half. The rows come from the same section as the worker's,
     * so the price is the shop's, not a guess.
     */
    async guess(query) {
      const catalog = await this.engine.catalog();
      if (this.lastQuery !== query || this.stage > STAGE.idle) return;

      const guessed = guessProducts(query, catalog);
      if (!guessed.length) {
        this.showLoading();
        return;
      }

      const note = this.texts.guessNote;
      await this.present(
        query,
        {
          handles: guessed.map((product) => product.handle),
          products: note ? guessed.map((product) => ({ handle: product.handle, reasons: [note] })) : [],
          answer: null,
          total: 0,
        },
        STAGE.guess
      );
    }

    /**
     * Put an answer on screen, whatever it is worth.
     *
     * Three things arrive out of order here, and the rule is that nothing ever
     * replaces something better. The handles are what the rows hang on: when
     * the final answer names the same products the partial did - which is the
     * normal case, the two are the same list - the rows stay exactly where they
     * are and only the block above them and the reasons change. Re-rendering
     * them would make the finished answer look like a second search.
     */
    async present(query, data, stage) {
      if (this.lastQuery !== query || stage < this.stage) return;

      const handles = data.handles || [];
      const key = handles.join(' ');

      /* Only once the answer is written: while the theme is guessing, every row
         carries the same "being checked" on purpose, and that belongs on the
         rows rather than above them. */
      const shared = stage === STAGE.final ? sharedReason(data.products || []) : null;
      const answer = this.answerBlock(data, shared) || (stage < STAGE.final ? this.answerBones() : '');

      if (key && key === this.renderedHandles) {
        this.clearLoading();
        this.stage = stage;
        this.querySelector('[data-ai-answer]')?.remove();
        if (answer) this.results.insertAdjacentHTML('afterbegin', answer);
        this.fillReasons(data.products || [], shared);
        if (stage === STAGE.final) this.trackShown(data);
        return;
      }

      let rows;
      try {
        rows = await this.engine.rows(handles);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.warn('ai-search: rows could not be rendered', error);
        if (this.lastQuery === query && stage === STAGE.final) this.showError(query);
        return;
      }

      if (this.lastQuery !== query || stage < this.stage) return;

      this.clearLoading();
      this.stage = stage;
      this.renderedHandles = key;
      this.results.innerHTML = `${answer}${rows}`;
      this.fillReasons(data.products || [], shared);
      if (stage === STAGE.final) this.trackShown(data);

      if (this.topFor === query) {
        this.topFor = null;
        requestAnimationFrame(() => this.scrollListToTop());
      }
    }

    /*
     * Back to the top of the list, the hard way, because Safari on the iPhone
     * ignored both scrollTo() and a plain scrollTop = 0:
     * - a list still coasting from a flick keeps coasting and overrides any
     *   position set in the meantime. Switching its overflow off for a frame
     *   stops the coast, then the position sticks.
     * - with the keyboard up, the iPhone can move the page itself instead of
     *   the list. The page goes back to where it stood when the search opened,
     *   so what is behind the overlay does not jump either.
     */
    scrollListToTop() {
      const list = this.querySelector('.ai-search__body');
      if (list) {
        list.style.overflowY = 'hidden';
        list.scrollTop = 0;
        requestAnimationFrame(() => {
          list.scrollTop = 0;
          list.style.overflowY = '';
        });
      }

      if (!this.matches?.('ai-search-overlay')) return;
      this.scrollTop = 0;
      if (typeof this.pageScrollY === 'number' && window.scrollY !== this.pageScrollY) {
        window.scrollTo(0, this.pageScrollY);
      }
    }

    /**
     * The reason a product matched is the one thing Liquid cannot know - it is
     * the model's judgement, not shop data. The section leaves a slot per row
     * and it gets filled here.
     *
     * On a set the first reason describes what is in the box and the rest is
     * the punchline, which is why the two are coloured differently; on a single
     * product the whole line is the match.
     */
    fillReasons(products, shared) {
      const byHandle = new Map(products.map((product) => [product.handle, product]));

      this.querySelectorAll('[data-handle]').forEach((row) => {
        const slot = row.querySelector('[data-ai-reason]');
        if (!slot) return;

        /* Said once above the answer already - repeating it under every row
           tells nobody which of them to pick. */
        const reasons = shared
          ? []
          : (byHandle.get(row.dataset.handle)?.reasons || [])
              .filter(Boolean)
              .map((reason) => this.localizeReason(reason));

        /* The row may be carrying the guess's "being checked" from a moment
           ago. Whatever it says now is what this answer says, including
           nothing. */
        if (!reasons.length) {
          slot.textContent = '';
          slot.closest('[data-ai-match]')?.removeAttribute('data-show');
          return;
        }

        if (row.hasAttribute('data-set') && reasons.length > 1) {
          const [lead, ...rest] = reasons;
          slot.innerHTML = `<span class="ai-search__lead">${escapeHtml(lead)}</span> · ${escapeHtml(rest.join(' · '))}`;
        } else {
          slot.textContent = reasons.join(' · ');
        }

        slot.closest('[data-ai-match]')?.toggleAttribute('data-show', true);
      });
    }

    /* ---------- the cart ---------- */

    /**
     * Straight into the cart, the way the rest of the theme does it: the
     * mini-cart names the sections it wants back, Shopify renders them into the
     * same reply as the cart itself, and the drawer swaps them in.
     *
     * Only a row Liquid marked as addable gets here - one available variant and
     * nothing to choose. Everything else is a plain link to the product page,
     * which is also where this lands if the request fails.
     */
    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const label = button.querySelector('[data-ai-button-label]');
      const previous = label?.textContent;

      button.setAttribute('data-busy', '');
      if (label && this.texts.adding) label.textContent = this.texts.adding;

      const miniCart = document.querySelector('mini-cart');
      const sections = miniCart ? miniCart.getSectionsToRender().map((section) => section.id) : [];

      try {
        const response = await fetch(theme.routes.cart_add_url, {
          ...fetchConfig('javascript'),
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1 }],
            sections,
            sections_url: window.location.pathname,
          }),
        });

        const state = await response.json();
        if (state.status) throw new Error(state.description || state.message || 'cart/add failed');

        /* Erst nach dem Hinzufügen: zwei Änderungen am selben Warenkorb
           gleichzeitig verlieren gern eine davon. */
        this.trackRow('add', button);

        /* The drawer opens itself at the end of renderContents, and it sits
           below this overlay - so the overlay gets out of the way first. */
        this.close();
        miniCart?.renderContents(state);

        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: state } }));
      } catch (error) {
        console.warn('ai-search: could not add to cart', error);
        window.location.href = button.getAttribute('href');
      } finally {
        button.removeAttribute('data-busy');
        if (label && previous) label.textContent = previous;
      }
    }

    /* ---------- Auswertung ---------- */

    /* Einmal je Frage, wenn die fertige Antwort auf dem Schirm steht. */
    trackShown(data) {
      if (!data.searchId || data.searchId === this.shownSearchId) return;
      this.shownSearchId = data.searchId;
      track('shown', { sid: data.searchId, stage: 'final' });
    }

    /* Eine Zeile der Antwort, angeklickt oder in den Warenkorb gelegt. */
    trackRow(type, element) {
      const row = element.closest('.ai-search__row');
      if (!row) return false;

      const rows = [...this.results.querySelectorAll('.ai-search__row')];
      const searchId = this.engine.searchId;
      track(type, {
        sid: searchId,
        handle: row.dataset.handle,
        position: rows.indexOf(row) + 1,
        stage: STAGE_NAMES[this.stage],
      });
      rememberSearch(searchId);
      return true;
    }

    trackLink(link) {
      if (this.trackRow('click', link)) return;

      /* Die Beliebt-Leiste, bevor überhaupt gefragt wurde. */
      if (link.closest('.ai-search__item')) {
        track('popular', { handle: handleFromUrl(link.getAttribute('href')) });
        return;
      }

      /* Der Weg zurück in die normale Suche, wenn der Worker nicht antwortet. */
      if (link.matches('.ai-search__button')) track('fallback', { sid: this.engine.searchId });
    }

    /* Hinweise und Rückfragen beantwortet das Theme selbst, ohne den Worker.
       Getippt wird währenddessen weiter, also zählt derselbe Fall einmal. */
    trackLocal(type, query) {
      const key = `${type}:${fold(query)}`;
      if (this.lastLocal && key.startsWith(this.lastLocal)) {
        this.lastLocal = key;
        return;
      }
      this.lastLocal = key;
      track(type, { query });
    }

    /**
     * The worker writes the first reason as "passt: <need>" - in German, on
     * every storefront, because the prefix is a literal in its code and only
     * the need itself comes from the model. Until it hands the need back on its
     * own, the German word is swapped for the one in this theme's locale file,
     * so an English shopper is not read a German label.
     */
    localizeReason(reason) {
      const prefix = this.texts.reasonPrefix;
      if (!prefix) return reason;

      return reason.replace(/^passt:\s*/i, `${prefix} `);
    }

    /* Above the answer the label supplies its own wording, so the worker's
       German "passt:" comes off rather than being translated. */
    stripReasonPrefix(reason) {
      return reason.replace(/^passt:\s*/i, '');
    }

    /*
     * Hier stand einmal "Noch X Treffer".
     *
     * Die Zeile rechnete data.total minus die gerenderten Zeilen. Der Worker
     * setzt total aber gleich der Anzahl, die er schickt - nicht gleich dem,
     * was der Shop hergibt. Die Differenz war deshalb immer null und die Zeile
     * nie zu sehen. Sichtbar wurde sie erst, als die Zeilen anfingen, Produkte
     * für 0,00 € auszusortieren: dann zählte sie genau die, die niemand sehen
     * soll.
     *
     * Wieder aufnehmen lohnt erst, wenn der Worker eine echte Gesamtzahl
     * meldet. Aus der Zahl der gerenderten Zeilen ist sie nicht zu gewinnen.
     */
  }

  /**
   * The same answer, on the search results page.
   *
   * The page gets here when the shop's own word search came back nearly empty -
   * someone typed a sentence into the header field and pressed Enter, and
   * Shopify matched it against product names. The block stands above the few
   * hits there are and says what the sentence was actually asking for.
   *
   * It borrows the overlay's methods rather than copying them: the rows, the
   * reasons and the cart button have to behave the same in both places, and two
   * copies of that drift apart the first time one of them is fixed.
   */
  class AiSearchPage extends HTMLElement {
    connectedCallback() {
      this.engine = new SearchEngine('page');
      this.texts = config().texts || {};
      this.flags = config().flags || [];
      this.clarifiers = config().clarifiers || [];
      this.known = (this.dataset.known || '').split(',').filter(Boolean);
      this.stage = STAGE.idle;
      this.renderedHandles = '';
      this.lastQuery = '';

      /* The results container is this element itself - there is no overlay
         around it and nothing to close. */
      this.results = this;

      this.addEventListener('click', (event) => {
        const add = event.target.closest('[data-ai-add]');
        if (add) {
          event.preventDefault();
          this.addToCart(add);
          return;
        }

        const link = event.target.closest('a[href]');
        if (link) this.trackLink(link);
      });

      this.run(this.dataset.query || '');
    }

    disconnectedCallback() {
      clearTimeout(this.loadingTimer);
      this.engine?.abort();
    }

    async run(query) {
      if (!query) return this.remove();

      this.lastQuery = query;

      const flag = matchesFlag(query, this.flags);
      if (flag) {
        this.trackLocal('flag', query);
        return this.showFlag(flag);
      }

      this.showLoading();

      let data;
      try {
        data = await this.engine.ask(
          query,
          (partial) => {
            if (this.lastQuery === query && partial.handles.length) {
              this.present(query, partial, STAGE.partial);
            }
          },
          { via: 'enter' }
        );
      } catch (error) {
        if (error?.name === 'AbortError' && !this.engine.timedOut) return;
        /* There is already an answer on this page. The block leaves rather than
           reporting a fault on top of it - a question that ran out of time is no
           reason to put an error above results the shopper can already read. */
        return this.remove();
      }

      /* Whatever the page is showing anyway is not news. If nothing is left and
         there is no sentence either, the block has nothing to say. */
      const rest = data.handles.filter((handle) => !this.known.includes(handle));
      if (!rest.length && !data.answer?.headline) {
        if (await this.retryCorrected(query)) return;
        return this.remove();
      }

      await this.present(query, { ...data, handles: rest }, STAGE.final);
    }
  }

  /* The pieces that behave identically in both places. */
  for (const method of [
    'present',
    'answerBlock',
    'answerBones',
    'showLoading',
    'clearLoading',
    'paintLoading',
    'showFlag',
    'retryCorrected',
    'fillReasons',
    'localizeReason',
    'stripReasonPrefix',
    'addToCart',
    'trackShown',
    'trackRow',
    'trackLink',
    'trackLocal',
  ]) {
    AiSearchPage.prototype[method] = AiSearchOverlay.prototype[method];
  }

  /* On the page there is nothing to close and no error state to fall back to -
     the block simply goes. */
  AiSearchPage.prototype.close = function close() {};
  AiSearchPage.prototype.showError = function showError() {
    this.remove();
  };

  if (!customElements.get('ai-search-overlay')) {
    customElements.define('ai-search-overlay', AiSearchOverlay);
  }
  if (!customElements.get('ai-search-page')) {
    customElements.define('ai-search-page', AiSearchPage);
  }
})();
